import type { OperationalPermission, OperationalScope, ProductVertical } from "./operational-permissions";

export interface OperationalRoleTemplate {
  roleId: string;
  label: string;
  vertical: ProductVertical;
  /** Perfil de negocio al que aplica (business_profile FM / business_type BH). */
  businessProfiles: string[];
  permissions: OperationalPermission[];
  allowedModules: string[];
  hiddenModules: string[];
  dashboardWidgets: string[];
  quickActions: string[];
  operationalScope: OperationalScope;
  recommendedPlan?: "starter" | "pro" | "business";
}

const ALL_FM_OWNER: OperationalPermission[] = [
  "can_manage_orders", "can_manage_catalog", "can_override_stock", "can_print_kitchen",
  "can_manage_schedule", "can_send_campaigns", "can_view_customer_phone", "can_manage_billing",
  "can_manage_team", "can_manage_branch", "can_operate_pos", "can_approve_pos_product_edit", "can_view_metrics",
  "can_manage_integrations", "can_manage_branding", "can_refund_order", "can_manage_tables",
];

export const FM_OPERATIONAL_ROLE_TEMPLATES: OperationalRoleTemplate[] = [
  {
    roleId: "owner",
    label: "Dueño / Gerente",
    vertical: "fm",
    businessProfiles: ["*"],
    permissions: ALL_FM_OWNER,
    allowedModules: ["*"],
    hiddenModules: [],
    dashboardWidgets: ["orders_today", "revenue", "low_stock", "pending_payments"],
    quickActions: ["new_order", "publish_store", "invite_team"],
    operationalScope: "tenant",
    recommendedPlan: "business",
  },
  {
    roleId: "cashier",
    label: "Caja",
    vertical: "fm",
    businessProfiles: ["fast_food", "restaurant", "market", "pharmacy", "catalog", "fashion", "bazar", "dietetics", "pet", "cafe"],
    permissions: ["can_manage_orders", "can_operate_pos", "can_view_customer_phone", "can_print_kitchen", "can_manage_tables"],
    allowedModules: ["/app/ordenes", "/app/pesaje", "/app/pagos"],
    hiddenModules: ["/app/metrics", "/app/settings", "/app/fidelizacion"],
    dashboardWidgets: ["orders_queue", "payments_pending"],
    quickActions: ["new_order", "charge"],
    operationalScope: "branch",
    recommendedPlan: "pro",
  },
  {
    roleId: "kitchen",
    label: "Cocina / Preparación",
    vertical: "fm",
    businessProfiles: ["fast_food", "restaurant", "cafe"],
    permissions: ["can_manage_orders", "can_print_kitchen"],
    allowedModules: ["/app/ordenes", "/app/operaciones", "/app/kds"],
    hiddenModules: ["/app/metrics", "/app/settings", "/app/pagos", "/app/clientes", "/app/fidelizacion"],
    dashboardWidgets: ["prep_queue", "delayed_orders"],
    quickActions: ["mark_ready"],
    operationalScope: "kitchen",
    recommendedPlan: "pro",
  },
  {
    roleId: "delivery",
    label: "Entregas",
    vertical: "fm",
    businessProfiles: ["fast_food", "restaurant", "pharmacy"],
    permissions: ["can_manage_orders", "can_view_customer_phone"],
    allowedModules: ["/app/operaciones", "/app/ordenes"],
    hiddenModules: ["/app/catalogacion", "/app/metrics", "/app/settings"],
    dashboardWidgets: ["deliveries_today"],
    quickActions: ["mark_delivered"],
    operationalScope: "branch",
  },
  {
    roleId: "branch_manager",
    label: "Encargado de sucursal",
    vertical: "fm",
    businessProfiles: ["*"],
    permissions: [
      "can_manage_orders", "can_manage_catalog", "can_override_stock", "can_print_kitchen",
      "can_view_customer_phone", "can_operate_pos", "can_approve_pos_product_edit", "can_view_metrics", "can_manage_branch",
      "can_manage_tables",
    ],
    allowedModules: ["/app/ordenes", "/app/operaciones", "/app/catalogacion", "/app/inventario", "/app/clientes", "/app/metrics"],
    hiddenModules: ["/app/settings"],
    dashboardWidgets: ["orders_today", "branch_kpis"],
    quickActions: ["new_order", "stock_adjust"],
    operationalScope: "branch",
    recommendedPlan: "business",
  },
  {
    roleId: "pharmacy_counter",
    label: "Mostrador",
    vertical: "fm",
    businessProfiles: ["pharmacy"],
    permissions: ["can_manage_orders", "can_operate_pos", "can_view_customer_phone"],
    allowedModules: ["/app/ordenes", "/app/catalogacion", "/app/clientes"],
    hiddenModules: ["/app/metrics", "/app/fidelizacion"],
    dashboardWidgets: ["orders_queue"],
    quickActions: ["new_order"],
    operationalScope: "branch",
  },
];

export const BH_OPERATIONAL_ROLE_TEMPLATES: OperationalRoleTemplate[] = [
  {
    roleId: "owner",
    label: "Dueño",
    vertical: "bh",
    businessProfiles: ["*"],
    permissions: [
      "can_confirm_booking", "can_refund_order", "can_manage_schedule", "can_send_campaigns",
      "can_view_customer_phone", "can_manage_billing", "can_manage_team", "can_manage_branch",
      "can_view_metrics", "can_manage_integrations", "can_manage_branding",
    ],
    allowedModules: ["*"],
    hiddenModules: [],
    dashboardWidgets: ["bookings_today", "revenue", "no_shows"],
    quickActions: ["new_booking", "invite_team"],
    operationalScope: "tenant",
    recommendedPlan: "business",
  },
  {
    roleId: "receptionist",
    label: "Recepción",
    vertical: "bh",
    businessProfiles: ["beauty_center", "clinic", "health_center", "fitness", "wellness", "therapy", "coworking", "education"],
    permissions: ["can_confirm_booking", "can_manage_schedule", "can_view_customer_phone", "can_operate_pos"],
    allowedModules: ["/app/agenda", "/app/bookings", "/app/clients"],
    hiddenModules: ["/app/metrics", "/app/settings", "/app/billing"],
    dashboardWidgets: ["today_bookings", "pending_confirmations"],
    quickActions: ["new_booking", "check_in"],
    operationalScope: "branch",
    recommendedPlan: "pro",
  },
  {
    roleId: "professional",
    label: "Profesional",
    vertical: "bh",
    businessProfiles: ["beauty_center", "clinic", "health_center", "therapy", "wellness"],
    permissions: ["can_confirm_booking", "can_manage_schedule", "can_view_customer_phone"],
    allowedModules: ["/app/agenda", "/app/clients"],
    hiddenModules: ["/app/metrics", "/app/settings", "/app/billing"],
    dashboardWidgets: ["my_schedule"],
    quickActions: ["block_time"],
    operationalScope: "professional",
  },
  {
    roleId: "clinic_coordinator",
    label: "Coordinación clínica",
    vertical: "bh",
    businessProfiles: ["clinic", "health_center"],
    permissions: ["can_confirm_booking", "can_manage_schedule", "can_view_customer_phone", "can_send_campaigns"],
    allowedModules: ["/app/agenda", "/app/clients", "/app/bookings"],
    hiddenModules: ["/app/settings"],
    dashboardWidgets: ["today_bookings", "waitlist"],
    quickActions: ["assign_professional"],
    operationalScope: "department",
    recommendedPlan: "business",
  },
];

export function roleTemplatesForVertical(vertical: ProductVertical): OperationalRoleTemplate[] {
  return vertical === "fm" ? FM_OPERATIONAL_ROLE_TEMPLATES : BH_OPERATIONAL_ROLE_TEMPLATES;
}

export function roleTemplatesForProfile(
  vertical: ProductVertical,
  businessProfile: string,
): OperationalRoleTemplate[] {
  return roleTemplatesForVertical(vertical).filter(
    (t) => t.businessProfiles.includes("*") || t.businessProfiles.includes(businessProfile),
  );
}
